// @flow

/**
 * Cookie consent — central knobs.
 *
 * Ported from 999-web-app (src/presentation/components/CookieConsent). Everything
 * a non-author needs to tune the banner (expiry, revision, categories, policy
 * links) lives here so it can be changed in one place.
 *
 * Differences from 999.md, all driven by what business.999.md actually loads:
 *  - no `advertising` category (no ad networks on this app) — so the
 *    "accept only necessary" button keeps the library's native behaviour of
 *    granting zero optional categories;
 *  - no `performance` category — the only performance-ish cookie here is GA's
 *    `_gat`, declared under `analytics` together with the rest of GA.
 */

/**
 * How long the user's choice is remembered, in months. This number is shown to
 * the user in the preferences modal ("stored for X months") and is converted to
 * days for the consent cookie below. Keep both constants in sync.
 */
export const CONSENT_EXPIRY_MONTHS = 6;

/** ~6 months. Passed to the library as `cookie.expiresAfterDays`. */
export const CONSENT_EXPIRY_DAYS = 182;

/** Name of the cookie the library uses to persist the user's choice. */
export const CONSENT_COOKIE_NAME = 'cc_consent';

/**
 * Google Tag Manager container of business.999.md. It used to be hardcoded in
 * the static loader in public/index.html; since CS9-537 the container is
 * prior-blocked and injected from consentMode.js (`loadGtm`) only after the user
 * has made a banner choice, so the id lives here with the rest of the knobs.
 */
export const GTM_CONTAINER_ID = 'GTM-WGN77XB';

/**
 * Policy / consent revision.
 *
 * BUMP THIS (e.g. 1 -> 2) whenever the cookie-policy text materially changes.
 * The library compares it against the revision stored in the consent cookie and
 * re-shows the banner to every returning user when it no longer matches, forcing
 * fresh consent. It is also recorded server-side as `banner_revision`.
 */
export const POLICY_VERSION = 1;

/**
 * Legal pages linked from the consent modal description. They are published on
 * the main site, so these are absolute URLs (the locale prefix is added in
 * buildConfig.js / serverSync.js).
 */
export const LEGAL_ORIGIN = 'https://999.md';
export const PRIVACY_POLICY_PATH = '/info/privacy-policy';
export const COOKIE_POLICY_PATH = `${PRIVACY_POLICY_PATH}#pages-cookiepolitics`;
/** Site rules / terms of use — recorded in the simpals-id consent documents. */
export const TERMS_PATH = '/info/rules';

/**
 * The three consent categories used on business.999.md. `necessary` is always
 * enabled and read-only; the other two are optional and MUST default to OFF
 * (see buildConfig.js).
 */
export const CONSENT_CATEGORIES = {
  necessary: 'necessary',
  functionality: 'functionality',
  analytics: 'analytics',
};

/**
 * Parse a raw `cc_consent` cookie value (URL-encoded JSON written by
 * vanilla-cookieconsent) into the accepted category ids. Returns [] on any
 * missing/malformed input.
 */
export function parseConsentCategories(rawCookieValue: ?string): string[] {
  if (!rawCookieValue) return [];
  try {
    const parsed = JSON.parse(decodeURIComponent(rawCookieValue));
    if (!Array.isArray(parsed.categories)) return [];
    return parsed.categories.filter(category => typeof category === 'string');
  } catch (error) {
    return [];
  }
}

/**
 * Whether the user has granted a consent category. False before the user makes
 * any banner choice (no cookie = denied).
 */
export function isCategoryGranted(category: string): boolean {
  if (typeof document === 'undefined') return false;
  const row = document.cookie
    .split('; ')
    .find(part => part.indexOf(`${CONSENT_COOKIE_NAME}=`) === 0);
  return parseConsentCategories(
    row ? row.slice(CONSENT_COOKIE_NAME.length + 1) : undefined,
  ).includes(category);
}
